import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { __ } from '../translations';
import { container } from '@intigral/domain/lib/DataDomain';
import { DI } from '@intigral/constant/config/DIContainer';
import { IChannelSeriesHistoryService } from '@intigral/domain/lib/application/services/ChannelSeriesHistoryService';
import user from '../helpers/user';
import h, { getCountry } from '../helpers/common';
import mpx from '../actions/mpx_data';
import { useGetBookMarksQuery } from '@intigral/domain/lib/presentation/hooks/useGetBookMarksQuery';
import { isGuestUser } from '../config/guestMode';
import { rethrowAsError } from '../utils/asyncError';

/** Compare MPX / history / bookmark ids that may be full URLs or bare ids. */
const idsMatch = (a?: string | number | null, b?: string | number | null) => {
    if (a == null || b == null) return false;
    if (String(a) === String(b)) return true;
    const tail = (value: string | number) => String(value).split('/').filter(Boolean).pop();
    return Boolean(tail(a) && tail(a) === tail(b));
};

const getLatestHistoryEntry = (seriesHistory) => {
    const entries = seriesHistory?.seriesHistory;
    if (!Array.isArray(entries) || entries.length === 0) {
        return null;
    }
    return [...entries].sort((a, b) => (b?.ts || 0) - (a?.ts || 0))[0];
};

/** Latest seriesHistory entry (by ts) that still has a matching bookmark. */
const getLatestBookmarkedHistoryEntry = (seriesHistory, bookmarks) => {
    const historyEntries = Array.isArray(seriesHistory?.seriesHistory) ? seriesHistory.seriesHistory : [];
    if (!bookmarks?.length || historyEntries.length === 0) {
        return null;
    }

    return (
        [...historyEntries]
            .filter((entry) => bookmarks.some((bookmark) => idsMatch(bookmark?.paId, entry?.id)))
            .sort((a, b) => (b?.ts || 0) - (a?.ts || 0))[0] || null
    );
};

/**
 * Prefer the season for the most recently watched in-progress bookmark, then next-to-watch,
 * then latest history. Falls back to undefined so callers keep using the first available season.
 */
const getPreferredSeasonId = (seriesHistory, bookmarks) => {
    if (!seriesHistory) {
        return undefined;
    }

    const bookmarkedHistoryEntry = getLatestBookmarkedHistoryEntry(seriesHistory, bookmarks);
    if (bookmarkedHistoryEntry?.seasonId) {
        return bookmarkedHistoryEntry.seasonId;
    }

    if (seriesHistory.nextEpisode?.seasonId) {
        return seriesHistory.nextEpisode.seasonId;
    }

    return getLatestHistoryEntry(seriesHistory)?.seasonId;
};

const findSeasonById = (seasons, seasonId) => {
    if (!seasonId || !seasons?.length) {
        return null;
    }
    return seasons.find((season) => idsMatch(season?.Id, seasonId) || idsMatch(season?.value, seasonId)) || null;
};

const buildEpisodeFromHistory = (historyEpisode) => {
    if (!historyEpisode?.id) {
        return null;
    }
    return {
        Id: historyEpisode.id,
        paId: historyEpisode.id,
        title: historyEpisode.title,
        seasonNumber: historyEpisode.seasonNumber,
        episodeNumber: historyEpisode.episodeNumber,
        tvSeasonNumber: historyEpisode.seasonNumber,
        tvSeasonEpisodeNumber: historyEpisode.episodeNumber,
        programType: 'episode',
    };
};

const getSeriesPlayLabel = (latestWatched, seriesHistory, episodes) => {
    const watch_now = __('watch_now');
    const resume_watching = __('resume_watching');
    const watch_next = __('watch_next');
    const nextEpisode = seriesHistory?.nextEpisode || null;
    const pad = (n: number | string) => n?.toString()?.padStart(2, '0');
    if (latestWatched && Object.prototype.hasOwnProperty.call(latestWatched, 'paId')) {
        const seasonNumber = latestWatched?.tvSeasonNumber || latestWatched?.seasonNumber;
        const episodeNumber = latestWatched.episodeNumber || latestWatched.tvSeasonEpisodeNumber;
        const resumeEpisode =
            episodes?.find((epi) => idsMatch(epi?.Id, latestWatched?.paId) || idsMatch(epi?.Id, latestWatched?.Id)) ||
            latestWatched;
        return {
            title: `${resume_watching} S${pad(seasonNumber)}, E${pad(episodeNumber)}`,
            nextEpisode: resumeEpisode,
        };
    } else if (nextEpisode) {
        const seasonNumber = nextEpisode.seasonNumber;
        const episodeNumber = nextEpisode.episodeNumber;
        const nextEpisodeData =
            episodes?.find((epi) => idsMatch(epi?.Id, nextEpisode?.id)) || buildEpisodeFromHistory(nextEpisode);
        return {
            title: `${watch_next} S${pad(seasonNumber)}, E${pad(episodeNumber)}`,
            nextEpisode: nextEpisodeData,
        };
    } else {
        const firstEpisode = Array.isArray(episodes) && episodes.length > 0 ? episodes[0] : null;
        const seasonNumber = firstEpisode?.tvSeasonNumber || firstEpisode?.seasonNumber;
        const episodeNumber = firstEpisode?.tvSeasonEpisodeNumber || firstEpisode?.episodeNumber;
        if (seasonNumber && episodeNumber) {
            return {
                title: `${watch_now} S${pad(seasonNumber)}, E${pad(episodeNumber)}`,
                nextEpisode: null,
            };
        }
        return {
            title: null,
            nextEpisode: null,
        };
    }
};

export const fetchSeriesHistory = (seriesId) => {
    const currentUser = user.getUserInfo();
    const isGuest = isGuestUser(currentUser);
    return useQuery({
        queryKey: ['series-history', [seriesId]],
        staleTime: 10 * 1000,
        enabled: Boolean(seriesId) && !isGuest,
        queryFn: async () => {
            try {
                const channelSeriesHistoryService = container.resolve<IChannelSeriesHistoryService>(
                    DI.SERVICES.IChannelSeriesHistoryService
                );
                const seasonHistory = await channelSeriesHistoryService.getSeriesHistory(
                    user.getAPIKey(),
                    user.getProfileName(),
                    {
                        country: getCountry(),
                        device: h.getDevice(),
                        seriesId: String(seriesId),
                    },
                    user.getUserToken(),
                    user.getIdentityToken()
                );
                return seasonHistory?.data ?? null;
            } catch (error) {
                rethrowAsError(error, 'Failed to fetch series history');
            }
        },
        refetchOnWindowFocus: false,
    });
};

const fetchSeriesEpisodes = (seriesId, preferredSeasonId, historyReady = true) => {
    return useQuery({
        queryKey: ['episodes', seriesId, preferredSeasonId ?? 'default'],
        enabled: seriesId !== null && seriesId !== undefined && historyReady,
        queryFn: async () => {
            const seriesSeasons = await mpx.getSeasons(seriesId, `{PC}{${getCountry()}}`);
            if (!seriesSeasons?.length) {
                return [];
            }

            const preferredSeason = findSeasonById(seriesSeasons, preferredSeasonId);
            const seasonToLoad = preferredSeason || seriesSeasons[0];
            const episodes = await mpx.getEpisodes(seasonToLoad.Id);
            return episodes ?? [];
        },
        refetchOnWindowFocus: false,
    });
};

const resolveSeriesBookmarkedItem = (bookmarks, episodes, seriesHistory) => {
    const historyEntries = Array.isArray(seriesHistory?.seriesHistory) ? seriesHistory.seriesHistory : [];
    const latestBookmarkedHistory = getLatestBookmarkedHistoryEntry(seriesHistory, bookmarks);

    // Prefer the bookmark for the most recently watched history episode (by ts).
    const episodeBookmark =
        (latestBookmarkedHistory &&
            bookmarks?.find((bookmark) => idsMatch(latestBookmarkedHistory.id, bookmark?.paId))) ||
        bookmarks?.find((bookmark) => idsMatch(seriesHistory?.nextEpisode?.id, bookmark?.paId)) ||
        bookmarks?.find((bookmark) => episodes?.some((ep) => idsMatch(ep?.Id, bookmark?.paId)));

    if (!episodeBookmark) {
        return {};
    }

    const episodeDetails = episodes?.find((ep) => idsMatch(ep?.Id, episodeBookmark?.paId));
    const historyDetails =
        historyEntries.find((entry) => idsMatch(entry?.id, episodeBookmark?.paId)) || latestBookmarkedHistory;

    return {
        ...episodeBookmark,
        Id: episodeBookmark.paId,
        ...(historyDetails && {
            seasonNumber: historyDetails.seasonNumber,
            episodeNumber: historyDetails.episodeNumber,
            tvSeasonNumber: historyDetails.seasonNumber,
            tvSeasonEpisodeNumber: historyDetails.episodeNumber,
        }),
        ...episodeDetails,
        // Keep bookmark progress fields authoritative when merging MPX episode details.
        timecode: episodeBookmark.timecode ?? historyDetails?.timeCode,
        duration: episodeBookmark.duration ?? episodeDetails?.duration,
    };
};

export const useSeriesNextEpisode = (item) => {
    const currentUser = user.getUserInfo();
    const profileGuid = user.getProfile()?.profileGuid;
    const isGuest = isGuestUser(currentUser);
    const { data: bookmarks } = useGetBookMarksQuery({
        apiKey: user.getAPIKey(),
        profileGuid: isGuest ? undefined : profileGuid,
        authToken: user.getUserToken(),
        identityToken: user.getIdentityToken(),
        country: getCountry(),
        device: h.getDevice(),
        programType: 'all',
    });
    const historyQuery = fetchSeriesHistory(item?.Id);
    const { data } = historyQuery;

    const preferredSeasonId = useMemo(() => getPreferredSeasonId(data, bookmarks), [data, bookmarks]);

    // Wait for series history before picking a season so multi-season CTAs don't flash S01/first season.
    const shouldWaitForHistory = !isGuest && Boolean(item?.Id) && item?.programType === 'series';
    const historyReady = !shouldWaitForHistory || historyQuery.isFetched || historyQuery.isError;

    const { data: episodes } = fetchSeriesEpisodes(item?.Id, preferredSeasonId, historyReady);
    let bookmarkedItem = null;
    // all the commented code will be re-use once V3 apis are deployed after release
    if (item?.programType === 'movie' || item?.programType === 'episode') {
        const movieBookmark = bookmarks?.find(
            (bookmark) => bookmark?.paId === item?.paId || bookmark?.paId == item?.Id
        );
        bookmarkedItem = { ...movieBookmark, ...(movieBookmark && item) }; // Merging item details into the bookmark for movies;
    } else {
        bookmarkedItem = resolveSeriesBookmarkedItem(bookmarks, episodes, data);
    }
    const { title, nextEpisode } = getSeriesPlayLabel(bookmarkedItem, data, episodes || []);
    const percentageNumber =
        (Math.ceil(Number(bookmarkedItem?.timecode)) * 100) / Math.ceil(Number(bookmarkedItem?.duration)) || null;

    const bookmarkData = {
        isBookmared: Object.prototype.hasOwnProperty.call(bookmarkedItem, 'paId') ? true : false,
        percentageNumber: percentageNumber,
        timecode: bookmarkedItem?.timecode,
    };
    return { title, nextEpisode, bookmarkData };
};
